<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    maxWidth="800"
    :title="modalTitle"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
  >
    <v-form class="mt-5">
      <v-row>
        <v-col>
          <v-text-field
            v-bind:label="$t('Title')"
            v-model="widgetTitle"
            @blur="editingWidgetTitle = false"
            @focus="editingWidgetTitle = true"
          ></v-text-field>
        </v-col>
        <v-col>
          <ColorPicker :label="$t('Color')" v-model="model.color" />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <ColorPicker
            :label="`${$t('Color')} (${$t('ExpiredPlural')})`"
            v-model="model.expiredColor"
          />
        </v-col>
        <v-col>
          <ColorPicker
            :label="`${$t('TextColor')} (${$t('ExpiredPlural')})`"
            v-model="model.expiredTextColor"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <ColorPicker
            :label="`${$t('Color')} (${$t('ExpireInLessThan')})`"
            v-model="model.warningColor"
          />
        </v-col>
        <v-col>
          <ColorPicker
            :label="`${$t('TextColor')} (${$t('ExpireInLessThan')})`"
            v-model="model.warningTextColor"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <ColorPicker
            :label="`${$t('Color')} (${$t('Ok')})`"
            v-model="model.onTimeColor"
          />
        </v-col>
        <v-col>
          <ColorPicker
            :label="`${$t('TextColor')} (${$t('Ok')})`"
            v-model="model.onTimeTextColor"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="auto">
          <v-switch
            color="primary"
            v-model="model.showDocumentOnOpeningModal"
            v-bind:label="$t('ShowMainDocument')"
          ></v-switch>
        </v-col>
        <v-col cols="auto">
          <v-switch
            color="primary"
            v-model="model.enableHorizontalScrolling"
            v-bind:label="$t('EnableHorizontalScrolling')"
          ></v-switch>
        </v-col>
      </v-row>
      <v-select
        multiple
        v-model="model.contentTypeIDs"
        :items="userContentTypes"
        item-text="text"
        item-value="id"
        v-bind:label="$t('ContentTypes')"
        :error-messages="contentTypeIDsErrors"
        @change="$v.model.contentTypeIDs.$touch()"
        @blur="$v.model.contentTypeIDs.$touch()"
      ></v-select>
      <v-select
        multiple
        v-model="model.fieldsToShow"
        :items="availableFields"
        item-text="Title"
        item-value="Code"
        v-bind:label="`${$t('FieldsToShow')}`"
        :error-messages="fieldsToShowErrors"
        @change="$v.model.fieldsToShow.$touch()"
        @blur="$v.model.fieldsToShow.$touch()"
      ></v-select>
      <v-row>
        <v-col>
          <v-select
            v-model="model.stateFieldCode"
            :error-messages="stateFieldCodeErrors"
            @change="$v.model.stateFieldCode.$touch()"
            @blur="$v.model.stateFieldCode.$touch()"
            :items="availableStateFields"
            item-text="Title"
            item-value="Code"
            v-bind:label="`${$t('Field')} (${$t('State')})`"
          ></v-select>
        </v-col>
        <v-col>
          <v-select
            multiple
            v-model="model.stateFieldValue"
            :error-messages="stateFieldValueErrors"
            @change="$v.model.stateFieldValue.$touch()"
            @blur="$v.model.stateFieldValue.$touch()"
            :items="availableListElements"
            item-text="text"
            item-value="text"
            v-bind:label="$t('IsDifferentFrom')"
          ></v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-select
            v-model="model.dateFieldCode"
            :error-messages="dateFieldCodeErrors"
            @change="$v.model.dateFieldCode.$touch()"
            @blur="$v.model.stateFieldCode.$touch()"
            :items="availableDateFields"
            item-text="Title"
            item-value="Code"
            v-bind:label="`${$t('Field')} (${$t('Date')})`"
          ></v-select>
        </v-col>
        <v-col>
          <v-text-field
            v-model="model.warningDays"
            :label="`${$t('WarningDays')}`"
            min="1"
            step="1"
            type="number"
            :error-messages="warningDaysErrors"
            @change="$v.model.warningDays.$touch()"
            @blur="$v.model.warningDays.$touch()"
          ></v-text-field>
        </v-col>
      </v-row>
    </v-form>
  </base-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import ColorPicker from '@/components/widgets/ColorPicker';
import Settings from '@/ecm/models/widget/documentTracker/DocumentTrackerSettings';
import SearchService from '@/services/SearchService';
import FieldType from '@/ecm/variables/FieldType';

export default {
    components: {
        ColorPicker,
    },
    data() {
        return {
            fieldList: [],
            model: new Settings(),
            modal: {
                key: 'documentTrackerModal',
            },
            open: false,
            editingWidgetTitle: false,
        };
    },
    mixins: [validationMixin],
    validations: {
        model: {
            contentTypeIDs: { required },
            stateFieldCode: { required },
            stateFieldValue: { required },
            dateFieldCode: { required },
            warningDays: { required },
            fieldsToShow: { required },
        },
    },
    computed: {
        modalInstance() {
            return this.$refs[this.modal.key];
        },
        widgetTitle: {
            get() {
                if (!this.editingWidgetTitle && !this.model.title) {
                    return this.$t('WidgetDocumentTrackerTitle');
                }
                return this.model.title;
            },
            set(val) {
                this.model.title = val;
            },
        },
        modalTitle() {
            return `${this.widgetTitle} - ${this.$t('Settings')}`;
        },
        userContentTypes() {
            return this.$store.getters['account/userContentTypes'];
        },
        selectedContentTypes() {
            return this.model.contentTypeIDs;
        },
        availableFields() {
            var self = this;
            var fieldsInAllContentSelectedContentTypes = this.fieldList.filter((f) =>
                self.selectedContentTypes.every((ct) =>
                    f.ContentTypeIDs.includes(parseInt(ct))
                )
            );
            return fieldsInAllContentSelectedContentTypes;
        },
        availableStateFields() {
            return this.availableFields.filter(
                (df) => df.Type == FieldType.StaticList
            );
        },
        availableDateFields() {
            return this.availableFields.filter((df) => df.Type == FieldType.DateTime);
        },
        availableListElements() {
            var stateField = this.availableFields.find(
                (df) => df.Code == this.model.stateFieldCode
            );
            if (stateField) return stateField.ListElements;
            else return [];
        },
        contentTypeIDsErrors() {
            const errors = [];
            if (!this.$v.model.contentTypeIDs.$dirty) return errors;
            !this.$v.model.contentTypeIDs.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        stateFieldCodeErrors() {
            const errors = [];
            if (!this.$v.model.stateFieldCode.$dirty) return errors;
            !this.$v.model.stateFieldCode.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        stateFieldValueErrors() {
            const errors = [];
            if (!this.$v.model.stateFieldValue.$dirty) return errors;
            !this.$v.model.stateFieldValue.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        dateFieldCodeErrors() {
            const errors = [];
            if (!this.$v.model.dateFieldCode.$dirty) return errors;
            !this.$v.model.dateFieldCode.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        warningDaysErrors() {
            const errors = [];
            if (!this.$v.model.warningDays.$dirty) return errors;
            !this.$v.model.warningDays.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        fieldsToShowErrors() {
            const errors = [];
            if (!this.$v.model.fieldsToShow.$dirty) return errors;
            !this.$v.model.fieldsToShow.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
    },
    watch: {
        selectedContentTypes: {
            immediate: true,
            async handler() {
                this.fieldList = await this.getFields();
            },
        },
    },
    methods: {
        async getFields() {
            var fields = [];
            if (this.model.contentTypeIDs.length > 0) {
                fields = await SearchService.getAdvSearchFields(
                    this.model.contentTypeIDs
                );
            }
            return fields;
        },
        saveItem() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }
            this.$emit('on-save', this.model);
            this.hide();
        },
        edit(model) {
            this.$v.$reset();
            this.model = model;
            this.show();
        },
        show() {
            this.open = true;
        },
        hide() {
            this.open = false;
        },
    },
};
</script>
